import { formatBRL, formatPercent } from '@/lib/charts/formatters';
import {
  TOOLTIP_DIVIDER,
  TOOLTIP_TEXT_FAINT,
  TOOLTIP_TEXT_HEADING,
  TOOLTIP_TEXT_MUTED,
  TOOLTIP_TEXT_STRONG,
} from '@/lib/charts/theme';

interface TooltipParam {
  seriesName?: string;
  name?: string;
  value?: unknown;
  color?: string;
  marker?: string;
  axisValueLabel?: string;
  percent?: number;
  data?: unknown;
  dataIndex?: number;
}

interface RenderOptions {
  title?: string;
  showTotal?: boolean;
  hidePercent?: boolean;
  format?: (value: number) => string;
  /** Extra muted lines under the rows for the hovered axis point (e.g. the day's charges). */
  notes?: (dataIndex: number) => readonly string[];
}

function coerceNumber(v: unknown): number | null {
  if (typeof v === 'number') return Number.isNaN(v) ? null : v;
  if (Array.isArray(v) && v.length > 0) {
    const last = v[v.length - 1];
    return typeof last === 'number' ? last : null;
  }
  return null;
}

function escape(s: string): string {
  return s.replace(/[&<>"']/g, (c) =>
    c === '&' ? '&amp;' : c === '<' ? '&lt;' : c === '>' ? '&gt;' : c === '"' ? '&quot;' : '&#39;',
  );
}

function row(color: string | undefined, label: string, value: string, percent?: string): string {
  const dot = color
    ? `<span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:${color};margin-right:8px;flex-shrink:0;"></span>`
    : '';
  const pct = percent
    ? `<span style="color:${TOOLTIP_TEXT_FAINT};font-size:11px;margin-left:8px;">${percent}</span>`
    : '';
  return `<div style="display:flex;align-items:center;justify-content:space-between;gap:12px;padding:2px 0;">
    <span style="display:flex;align-items:center;color:${TOOLTIP_TEXT_MUTED};font-size:12px;">${dot}${escape(label)}</span>
    <span style="font-variant-numeric:tabular-nums;font-weight:500;color:${TOOLTIP_TEXT_STRONG};font-size:12px;">${value}${pct}</span>
  </div>`;
}

export function buildTooltip(opts: RenderOptions = {}) {
  const fmt = opts.format ?? formatBRL;
  return (rawParams: unknown): string => {
    const params = (Array.isArray(rawParams) ? rawParams : [rawParams]) as TooltipParam[];
    if (!params.length) return '';

    const title = opts.title ?? params[0]?.axisValueLabel ?? params[0]?.name ?? '';
    const header = title
      ? `<div style="font-size:11px;font-weight:600;color:${TOOLTIP_TEXT_HEADING};margin-bottom:6px;">${escape(String(title))}</div>`
      : '';

    let total = 0;
    const rows = params
      .map((p) => {
        const val = coerceNumber(p.value);
        if (val === null) return '';
        total += val;
        const label = p.seriesName || p.name || '';
        const pct = !opts.hidePercent && typeof p.percent === 'number' ? formatPercent(p.percent / 100) : undefined;
        return row(p.color, label, fmt(val), pct);
      })
      .filter(Boolean)
      .join('');

    const totalRow =
      opts.showTotal && params.length > 1
        ? `<div style="margin-top:6px;padding-top:6px;border-top:1px solid ${TOOLTIP_DIVIDER};">${row(undefined, 'Total', fmt(total))}</div>`
        : '';

    const dataIndex = params[0]?.dataIndex;
    const notes = opts.notes && typeof dataIndex === 'number' ? opts.notes(dataIndex) : [];
    const notesBlock = notes.length
      ? `<div style="margin-top:6px;padding-top:6px;border-top:1px solid ${TOOLTIP_DIVIDER};">${notes
          .map(
            (note) =>
              `<div style="color:${TOOLTIP_TEXT_MUTED};font-size:11px;padding:1px 0;">${escape(note)}</div>`,
          )
          .join('')}</div>`
      : '';

    return `<div style="min-width:160px;">${header}${rows}${totalRow}${notesBlock}</div>`;
  };
}
